import React, { useState, useRef, useEffect } from "react";
import dayjs from "@dayjs";
import { Dayjs } from "dayjs";
import { MAX_STAY_DURATION_DAYS } from "@/constants/stay";
import { ChevronLeft, ChevronRight } from "lucide-react";
import DateInput from "./FlightDateInput";
import type { FlightSearchRequest } from "@/types/flights";
import { useTheme } from "next-themes";
import { useLocale, useTranslations } from "next-intl";
import { getWeekdays } from "@/app/utils/date/getWeekdays";

type Props = {
  departOnly?: boolean;
  departDate: string;
  returnDate: string;
  updateFlightField: (
    flightId: string,
    updatedFields: Partial<FlightSearchRequest>
  ) => void;
  flightId: string;
};

const DesktopCalendars = ({
  departOnly = false,
  departDate,
  returnDate,
  updateFlightField,
  flightId,
}: Props) => {
  const t = useTranslations();
  const { resolvedTheme } = useTheme();
  const [activeInput, setActiveInput] = useState<"depart" | "return" | null>(
    null
  );
  const calendarRef = useRef<HTMLDivElement | null>(null);

  const locale = useLocale();

  const [currentMonth, setCurrentMonth] = useState(
    dayjs().locale(locale.toLowerCase()).startOf("month")
  );

  const isDarkMode = resolvedTheme === "dark";

  const onDateChange = (dates: [string | null, string | null]) => {
    const [depart, ret] = dates;
    const updatedFields: Partial<FlightSearchRequest> = {
      ...(depart !== null ? { departDate: depart } : {}),
      returnDate: ret == null ? "" : ret,
    };
    updateFlightField(flightId, updatedFields);
  };

  const departDayjs = departDate ? dayjs(departDate) : null;
  const returnDayjs = returnDate ? dayjs(returnDate) : null;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(event.target as Node)
      ) {
        setActiveInput(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (departOnly && returnDate) {
      updateFlightField(flightId, { returnDate: "" });
    }
  }, [departOnly, returnDate, updateFlightField, flightId]);

  const handleMonthChange = (direction: "prev" | "next") => {
    const earliestMonth = dayjs().startOf("month");
    const maxMonth = dayjs()
      .add(1, "year")
      .startOf("month")
      .subtract(1, "month");
    const newMonth =
      direction === "prev"
        ? currentMonth.subtract(1, "month")
        : currentMonth.add(1, "month");

    if (newMonth.isBefore(earliestMonth, "month")) {
      setCurrentMonth(earliestMonth);
    } else if (newMonth.isAfter(maxMonth, "month")) {
      setCurrentMonth(maxMonth);
    } else {
      setCurrentMonth(newMonth);
    }
  };

  const handleDayClick = (day: Dayjs) => {
    if (departOnly) {
      onDateChange([day.format("YYYY-MM-DD"), null]);
      setActiveInput(null);
      return;
    }

    if (activeInput === "depart") {
      onDateChange([day.format("YYYY-MM-DD"), null]);
      setActiveInput("return");
    } else if (activeInput === "return") {
      if (departDayjs) {
        if (day.isBefore(departDayjs, "day")) {
          onDateChange([day.format("YYYY-MM-DD"), null]);
          setActiveInput("return"); // Keep return input active
        } else {
          onDateChange([departDate, day.format("YYYY-MM-DD")]);
          setActiveInput("depart");
        }
      }
    }
  };

  const getDaysInMonth = (month: Dayjs) => {
    const startOfMonth = month.startOf("month");
    const endOfMonth = month.endOf("month");
    const startOfWeek = startOfMonth.startOf("week");
    const endOfWeek = endOfMonth.endOf("week");

    const days = [];
    let day = startOfWeek;
    while (day.isBefore(endOfWeek) || day.isSame(endOfWeek, "day")) {
      days.push(day);
      day = day.add(1, "day");
    }
    return days;
  };

  const isDayDisabled = (day: Dayjs) => {
    if (day.isBefore(dayjs(), "day")) return true;
    if (day.isAfter(dayjs().add(1, "year"), "day")) return true;
    if (!departOnly && activeInput === "return" && departDayjs) {
      return day.isAfter(departDayjs.add(MAX_STAY_DURATION_DAYS, "day"));
    }
    return false;
  };

  const isDaySelected = (day: Dayjs) => {
    const isToday = day.isSame(dayjs().startOf("day"), "day");

    if (!departDayjs) {
      return {
        isSelected: false,
        isDepart: false,
        isReturn: false,
        isBetween: false,
        isToday,
      };
    }

    if (departDayjs && !returnDayjs) {
      return {
        isSelected: day.isSame(departDayjs, "day"),
        isDepart: day.isSame(departDayjs, "day"),
        isReturn: false,
        isBetween: false,
        isToday,
      };
    }

    return {
      isSelected:
        day.isSame(departDayjs, "day") ||
        day.isSame(returnDayjs, "day") ||
        (returnDayjs &&
          day.isAfter(departDayjs, "day") &&
          day.isBefore(returnDayjs, "day")),
      isDepart: day.isSame(departDayjs, "day"),
      isReturn: returnDayjs ? day.isSame(returnDayjs, "day") : false,
      isBetween: returnDayjs
        ? day.isAfter(departDayjs, "day") && day.isBefore(returnDayjs, "day")
        : false,
      isToday,
    };
  };

  const weekdays = getWeekdays(t);

  return (
    <div className="relative w-full font-inter">
      <DateInput
        departOnly={departOnly}
        departDate={departDate}
        returnDate={returnDate}
        onClickDepart={() => setActiveInput("depart")}
        onClickReturn={() => {
          if (departOnly) return;
          if (departDate) {
            setActiveInput("return");
          } else {
            setActiveInput("depart");
          }
        }}
      />

      {(activeInput === "depart" ||
        (activeInput === "return" && !departOnly)) && (
        <div
          className="absolute z-50 mt-4 rounded-[16px] sm:rounded-xl p-[2px] bg-sleap-gradiant"
          style={{ left: "50%", transform: "translateX(-50%)" }}
        >
          <div
            ref={calendarRef}
            className="bg-lightwhite dark:bg-sleap-gray-950 shadow-rooms-card rounded-[14px] sm:rounded-[10px] flex flex-col gap-4 p-0"
          >
            <div className="p-4 flex flex-col w-[880px] relative">
              <button
                type="button"
                onClick={() => handleMonthChange("prev")}
                disabled={currentMonth.isSame(
                  dayjs().startOf("month"),
                  "month"
                )}
                className="p-2 rounded-full outline outline-1 outline-sleap-off-white-100 dark:outline-sleap-gray-700 outline-offset-[-1px] disabled:opacity-50 disabled:cursor-not-allowed absolute left-4"
              >
                <ChevronLeft
                  size={24}
                  className={
                    isDarkMode ? "stroke-lightwhite" : "stroke-sleap-gray-950"
                  }
                />
              </button>
              <button
                type="button"
                onClick={() => handleMonthChange("next")}
                disabled={currentMonth
                  .add(1, "month")
                  .isSame(
                    dayjs()
                      .add(1, "year")
                      .startOf("month")
                      .subtract(1, "month"),
                    "month"
                  )}
                className="p-2 rounded-full outline outline-1 border-sleap-off-white-100 dark:border-sleap-gray-700 outline-offset-[-1px] disabled:opacity-50 disabled:cursor-not-allowed absolute right-4"
              >
                <ChevronRight
                  size={24}
                  className={
                    isDarkMode ? "stroke-lightwhite" : "stroke-sleap-gray-950"
                  }
                />
              </button>

              <div className="flex gap-8">
                {[currentMonth, currentMonth.add(1, "month")].map(
                  (month, index) => (
                    <div key={index} className="flex flex-col gap-0.5 w-full">
                      <div className="flex flex-col items-center mb-4">
                        <span className="text-sleap-gray-950 dark:text-lightwhite text-[14px] font-bold leading-[24px]">
                          {month.locale(locale.toLowerCase()).format("MMMM")}
                        </span>
                        <span className="text-sleap-gray-950 dark:text-lightwhite text-[14px] font-medium leading-[24px]">
                          {month.locale(locale.toLowerCase()).format("YYYY")}
                        </span>
                      </div>
                      <div className="flex gap-0.5 mb-0.5">
                        {weekdays.map((day) => (
                          <div
                            key={day}
                            className="w-full h-[48px] flex items-center justify-center text-[14px]"
                          >
                            {day}
                          </div>
                        ))}
                      </div>
                      <div className="grid grid-cols-7 gap-y-0.5 text-center">
                        {getDaysInMonth(month).map((day, i) => {
                          const isCurrentMonth = day.month() === month.month();
                          const isDisabled = isDayDisabled(day);
                          const {
                            isSelected,
                            isDepart,
                            isReturn,
                            isBetween,
                            isToday,
                          } = isDaySelected(day);

                          if (!isCurrentMonth) {
                            return (
                              <div
                                key={i}
                                className="p-3 opacity-0 flex items-center justify-center w-full h-full"
                              />
                            );
                          }

                          return (
                            <div
                              className={` ${isSelected && isDepart && "ms-3"}
                             ${isSelected && isReturn && "me-3"}`}
                              key={i}
                            >
                              <div
                                key={i}
                                onClick={() =>
                                  !isDisabled && handleDayClick(day)
                                }
                                className={`p-3  w-full h-full cursor-pointer text-base flex items-center justify-center min-h-[48px] max-h-[48px] relative
                              ${isDisabled ? "cursor-not-allowed text-sleap-gray-400 dark:text-sleap-gray-600" : ""}
                              ${!isSelected && !isDisabled ? "hover:bg-sleap-gray-100 dark:hover:bg-sleap-gray-700 rounded-full" : ""}
                           ${isSelected && (isDepart || isReturn) && ` ${!departOnly ? "dark:bg-sleap-purple-350 bg-sleap-purple-350" : ""}`}

                             ${isSelected && isDepart && "rounded-l-full pl-0 relative"}
                             ${isSelected && isReturn && "rounded-r-full pr-0"}
                              `}
                              >
                                {isSelected && (isDepart || isReturn) && (
                                  <div className="absolute h-12 top-0 bg-sleap-purple-300 rounded-full w-12 z-10" />
                                )}
                                {isBetween && (
                                  <div className="absolute h-12 top-0 bg-sleap-purple-350 dark:bg-sleap-purple-350 w-full z-10" />
                                )}
                                <div className="relative z-30">
                                  <span
                                    className={`${
                                      isDepart || isReturn
                                        ? "text-white dark:text-sleap-fern-100 font-semibold"
                                        : isToday && !isDisabled
                                          ? "text-sleap-purple-300 dark:text-sleap-purple-300"
                                          : isBetween
                                            ? "text-sleap-gray-950 dark:text-lightwhite"
                                            : ""
                                    }`}
                                  >
                                    {day.date()}
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DesktopCalendars;
